import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const HERO_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/1bn6mxi9_hero%20page%20new%20image.png';

const lineReveal = {
  hidden: { y: '110%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const FitnessHero = () => {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section data-testid="fitness-hero" id="hero" className="relative bg-black pt-28 pb-12 sm:pb-16 lg:pb-20 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Left copy */}
          <div>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              data-testid="back-to-home"
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-white text-xs tracking-[0.3em] uppercase mb-6 transition-colors"
            >
              ← The Dillon Blueprint
            </motion.button>

            <p className="text-gray-400 font-bold text-xs sm:text-sm tracking-[0.35em] uppercase mb-5">
              Fitness Coaching
            </p>

            <h1
              data-testid="fitness-headline"
              aria-label="Strong. Lean. Athletic. Again."
              className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] uppercase"
            >
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
                  Strong. Lean.
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span
                  custom={1}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                  className="block bg-gradient-to-b from-white via-white to-neutral-400 bg-clip-text text-transparent"
                >
                  Athletic. Again.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="text-gray-300 text-base md:text-lg mt-6 max-w-xl leading-relaxed"
            >
              Structured training, real accountability, and an athlete's mindset built for women 30+ who
              want to feel capable in their bodies again.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.0 }}
              className="flex flex-col sm:flex-row gap-4 mt-8"
            >
              <button
                data-testid="hero-explore-coaching"
                onClick={() => scrollTo('coaching')}
                className="btn-shine bg-gray-300 hover:bg-white text-black font-bold px-8 py-4 rounded-lg transition-colors"
              >
                <span className="relative z-10">Explore Coaching</span>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.15 }}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-8 text-gray-400 text-sm"
            >
              <span>Strength</span>
              <span className="text-gray-600">•</span>
              <span>Functional Movement</span>
              <span className="text-gray-600">•</span>
              <span>Consistency</span>
            </motion.div>
          </div>

          {/* Right image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative rounded-2xl overflow-hidden border border-neutral-800 aspect-[4/3] lg:aspect-[5/6]">
              <img
                src={HERO_IMG}
                alt="Coach Alou training a fitness client"
                className="absolute inset-0 w-full h-full object-cover ken-burns"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
